import { InputType } from './enum';
import { TCurrency } from './types';

const numberFormatter = require('number-formatter');

export interface IFormatOutput {
  formatted: string;
  parsed: string | number;
}

export type Decimals = 0 | 1 | 2 | 3 | 4;

export const formatNumberInput = (
  value: string,
  inputType: InputType,
  decimals: Decimals = 0,
  allowNegative: boolean = false
) => {
  let filtered = value
    ?.replace(/\s/g, '') // Whitespace
    ?.replace(String.fromCodePoint(8722), '-') // Minus sign
    ?.replace(',', inputType === InputType.FLOAT ? '.' : ''); // Decimal separator

  if (!allowNegative) {
    filtered = filtered?.replace(/-/g, '');
  }

  if (allowNegative && filtered === '-') {
    return {
      formatted: '-',
      parsed: null,
    };
  }

  let parsed: number =
    inputType === InputType.FLOAT
      ? Number.parseFloat(Number.parseFloat(filtered).toFixed(decimals))
      : Number.parseInt(filtered);

  if (Number.isNaN(parsed))
    return {
      formatted: '',
      parsed: null,
    };

  let formatted: string = formatNumber(parsed, inputType === InputType.FLOAT ? decimals : 0);

  if (inputType === InputType.FLOAT) {
    // Ends with decimal separator
    if (!filtered.slice(0, -1).includes('.') && filtered.endsWith('.')) formatted += ',';
    // Only decimal is zero
    else if (filtered.endsWith('.0')) formatted += ',0';
  }

  return {
    formatted,
    parsed,
  };
};

/**
 * Format value as a date.
 * If parsing fails, return original value.
 */
export const formatDateInput = (value: string) => {
  let parsedDate = new Date(value);

  let formatted = value,
    parsed = value;
  if (!Number.isNaN(parsedDate.getTime())) {
    const day = parsedDate.getDate()?.toString()?.padStart(2, '0') ?? '';
    const month = (parsedDate.getMonth() + 1)?.toString()?.padStart(2, '0') ?? '';
    const year = parsedDate?.getFullYear()?.toString() ?? '';

    formatted = `${day}.${month}.${year}`;
    parsed = `${year}-${month}-${day}`;
  }

  return {
    formatted,
    parsed,
  };
};

/* Formats a date string in the format "dd.mm, hh:mm".
   Returns an empty string if the input is invalid or undefined
*/
export const formatReservationDate = (value?: string | null) => {
  if (!value) {
    return '';
  }

  const reservationDate = new Date(value);
  if (Number.isNaN(reservationDate.getTime())) {
    return '';
  }

  const day = reservationDate.getDate();
  const month = reservationDate.getMonth() + 1;
  const hours = String(reservationDate.getHours()).padStart(2, '0');
  const minutes = String(reservationDate.getMinutes()).padStart(2, '0');

  return `${day}.${month}, ${hours}:${minutes}`;
};

export const formatLicensePlateInput = (value: string) => {
  let formatted = '';
  let parsed = value?.replace('\xa0', '') ?? null;

  // The first two characters contain a non-letter symbol
  if (parsed?.length <= 2 && parsed?.match(/[^A-Z-a-z]/g)) {
    parsed = parsed?.match(/[A-Za-z]/g)?.join('') ?? '';
  }

  // A number was not entered as the third letter
  if (parsed?.length === 3 && !parsed?.slice(-1).match(/\d/g)) {
    parsed = parsed.slice(0, -1);
  }

  if (parsed?.length >= 3) {
    const letterCombination = parsed.slice(0, 2);
    const numberSeries = parsed.slice(2);
    formatted = `${letterCombination}\xa0${numberSeries}`;
  } else {
    formatted = parsed;
  }

  return {
    formatted: formatted?.toUpperCase() ?? '',
    parsed: parsed?.toUpperCase() ?? '',
  };
};

export const formatOrganizationNumberInput = (value: string) => {
  const parsed = value?.replace(/(\xa0)/g, '')?.replace(/[^0-9]/g, '') || null;
  const formatted = parsed
    ? parsed
        .split('')
        .map((char, i) => {
          return (i + 1) % 3 === 0 && i + 1 < parsed.length ? `${char}\xa0` : char;
        })
        .join('')
    : '';

  return {
    formatted,
    parsed,
  };
};

/**
 * Specify whether the country code should be permitted input or not.
 *
 * We only allow Norwegian phone numbers since only they work with "BankID på mobil".
 */
export const formatPhoneNumberInput = (value: string, withCountryCode: boolean = false) => {
  // Used for differentiating between adding or removing text
  const separator = withCountryCode ? 'X' : '';
  let removing = false;

  const regex = new RegExp(`[^\\d${separator}${withCountryCode ? '+' : ''}]`, 'g');
  let parsed = value
    ?.replace('\xa0', separator) // Replaces first occurrence
    ?.replace(/(\xa0)/g, '') // Replaces remaining
    ?.replace(regex, '');
  let countryCode = '';
  let phoneNumber = parsed ?? '';

  if (withCountryCode && phoneNumber?.startsWith('+')) {
    countryCode = parsed.slice(0, 3);
    phoneNumber = parsed.slice(3).padStart(separator.length, separator);

    // Country code is not Norway
    if (
      (countryCode.length === 2 && countryCode.slice(-1) !== '4') ||
      (countryCode.length === 3 && countryCode.slice(-1) !== '7')
    ) {
      countryCode = countryCode.slice(0, -1);
      parsed = countryCode;
    }

    // Is the user removing text?
    removing = countryCode.length === 3 && countryCode === '+47' && phoneNumber === separator;
  }

  // Cleanup
  phoneNumber = phoneNumber?.replace(separator, '');
  parsed = parsed?.replace(separator, '');

  // Add separators every two digits
  phoneNumber = phoneNumber
    ?.split('')
    ?.map((digit, i) => {
      return (i + 1) % 2 === 0 && i + 1 < phoneNumber.length ? `${digit}\xa0` : digit;
    })
    ?.join('');

  const formatted = `${countryCode}${countryCode.length === 3 && !removing ? '\xa0' : ''}${phoneNumber}`;

  return {
    formatted: formatted ?? '',
    parsed: parsed ?? null,
  };
};

export const formatPersonalIdentityNumber = (value: string) => {
  const parsed = value?.replace(/(\xa0)/g, '')?.replace(/[^0-9]/g, '') || null;

  const birthDate = parsed?.slice(0, 6);
  const checkDigits = parsed?.slice(6);
  const formatted = `${birthDate ? birthDate : ''}${checkDigits ? '\xa0' + checkDigits : ''}`;

  return {
    formatted: formatted ?? '',
    parsed: parsed ?? null,
  };
};

/**
 * Format time with a colon as a separator.
 * We do not include the seconds component.
 */
export const formatTimeInput = (value: string) => {
  const [hour, minute] = value?.split(':')?.map((value: string, i) =>
    value.match(/\d+/g)
      ? Math.min(parseInt(value), i === 0 ? 23 : 59)
          .toString()
          .padStart(2, '0')
      : ''
  ) ?? ['', ''];

  const formatted = hour ? `${hour.length >= 2 ? hour + ':' : hour}${minute ? minute : ''}` : '';
  const parsed = formatted || '';

  return {
    formatted,
    parsed,
  };
};

export const formatZipCode = (value: string) => {
  const parsed = value?.match(/\d+/g)?.toString() || '';
  const formatted = parsed;

  return {
    formatted,
    parsed,
  };
};

export const formatEmailInput = (value: string) => {
  let parsed =
    value
      ?.replace(/(\w+)@+(\w*)/, '$1@$2') // Multiple @ symbols
      ?.replace(/(\w+@\w+)\.+/, '$1.') ?? ''; // Multiple periods
  const formatted = parsed;

  return {
    formatted,
    parsed,
  };
};

export const formatDate = (timestamp: string) => {
  if (timestamp === null) return null;
  let td = new Date(timestamp);
  let date = td.getDate().toString();
  if (date.length === 1) {
    date = `0${date}`;
  }
  let month = (td.getMonth() + 1).toString();
  if (month.length === 1) {
    month = `0${month}`;
  }
  let year = td.getFullYear().toString();
  return date + '.' + month + '.' + year;
};

export const formatTime = (timestamp: string, includeSeconds: boolean = true) => {
  if (timestamp === null) return null;
  const td = new Date(timestamp);
  const hours = td.getHours() < 10 ? `0${td.getHours()}` : td.getHours();
  const minutes = td.getMinutes() < 10 ? `0${td.getMinutes()}` : td.getMinutes();
  const seconds = td.getSeconds() < 10 ? `0${td.getSeconds()}` : td.getSeconds();
  return `${hours}:${minutes}:${seconds}`;
};

export const formatYearInput = (value: string) => {
  const digitsOnly = value?.replace(/\xa0/g, '')?.replace(/\s/g, '')?.replace(/\D/g, '') ?? '';
  const limited = digitsOnly.slice(0, 4);

  return {
    formatted: limited,
    parsed: limited.length === 4 ? limited : null,
  };
};

export const capitalizeFirstLetter = (text: string) => {
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const lowerCaseFirstLetter = (text: string) => {
  return text.charAt(0).toLowerCase() + text.slice(1);
};

export const objectKeysToUpperCase = (input: any) => {
  return objectKeysMapper(input, capitalizeFirstLetter);
};

export const objectKeysToLowerCase = (input: any): any => {
  return objectKeysMapper(input, lowerCaseFirstLetter);
};

const objectKeysMapper = (input: any, mapperFunction: (text: string) => string): any => {
  if (!input || typeof input !== 'object') {
    return input;
  }
  if (Array.isArray(input)) {
    return input.map((val) => objectKeysMapper(val, mapperFunction));
  }
  return Object.keys(input).reduce((newObj: Record<string, any>, key) => {
    const val = input[key];
    newObj[mapperFunction(key)] = !!val && typeof val === 'object' ? objectKeysMapper(val, mapperFunction) : val;
    return newObj;
  }, {});
};

// TODO: Deprecated
export const formatNumber = (num: number, fractionDigits: Decimals = 0) => {
  if (typeof num !== 'number') return num;
  return numberFormatter('### ##0,####', parseFloat(num.toFixed(fractionDigits)));
};

export enum InputTagType {
  button,
  checkbox,
  color,
  date = 'date',
  'datetime-local' = 4,
  email = 'email',
  file = 6,
  hidden,
  image,
  month = 'month',
  number = 10,
  password,
  radio,
  range,
  reset,
  search,
  submit,
  tel = 'tel',
  text = 'text',
  time = 'time',
  url = 20,
  week,
}
export const getInputTagType = (type: InputType): string => {
  switch (+type) {
    case InputType.DATE:
      return InputTagType.date;
    case InputType.EMAIL:
      return InputTagType.email;
    case InputType.INTEGER:
    case InputType.ORGANIZATION_NUMBER:
    case InputType.PERSONAL_IDENTITY_NUMBER:
    case InputType.PHONE_NUMBER:
    case InputType.ZIP_CODE:
      return InputTagType.tel;
    case InputType.TIME:
      return InputTagType.time;
    case InputType.MONTH:
      return InputTagType.month;
    case InputType.FLOAT:
    case InputType.LICENSE_PLATE:
    case InputType.TEXT_AREA:
    case InputType.TEXT_LINE:
    case InputType.COPY_TEXT:
    default:
      return InputTagType.text;
  }
};

export const formatInput = (
  value: string,
  type: InputType,
  decimals: Decimals,
  allowNegative: boolean = false
): IFormatOutput => {
  switch (+type) {
    case InputType.EMAIL:
      return formatEmailInput(value);
    case InputType.FLOAT:
      return formatNumberInput(value, type, decimals, allowNegative);
    case InputType.INTEGER:
      return formatNumberInput(value, type, 0, allowNegative);
    case InputType.LICENSE_PLATE:
      return formatLicensePlateInput(value);
    case InputType.ORGANIZATION_NUMBER:
      return formatOrganizationNumberInput(value);
    case InputType.PERSONAL_IDENTITY_NUMBER:
      return formatPersonalIdentityNumber(value);
    case InputType.PHONE_NUMBER:
      return formatPhoneNumberInput(value);
    case InputType.ZIP_CODE:
      return formatZipCode(value);
    case InputType.YEAR:
      return formatYearInput(value);

    case InputType.DATE: // Chrome includes a date picker for type="date"
    case InputType.TEXT_AREA:
    case InputType.TEXT_LINE:
    case InputType.TIME: // Chrome includes a time picker for type="time"
    case InputType.MONTH:
    default:
      return { formatted: value, parsed: value };
  }
};

export const formatPrice = (price: TCurrency): string => {
  return formatInput(`${price.value}`, InputType.INTEGER, 0).formatted + ' kr';
};
